export const queryParamUtils = {
    // ------------------------
    // GETTERS
    // ------------------------
    /**
     * Get the value of a specific query parameter by key.
     *
     * @param key - The name of the query parameter to retrieve.
     * @param options - { decode?: boolean } - whether to decode the value (default: true).
     * @returns The query param value (decoded if specified), or null if not found.
     *
     * @example
     * // URL: ?token=abc%20123
     * queryParamUtils.get('token'); // 'abc 123'
     */
    get(key, options = {}) {
        if (typeof window === 'undefined')
            return null;
        const { decode = true } = options;
        const value = new URLSearchParams(window.location.search).get(key);
        return value && decode ? decodeURIComponent(value) : value;
    },
    /**
     * Get a specific query parameter from a full URL string.
     *
     * @param url - The full URL to extract the parameter from.
     * @param key - The name of the query parameter to retrieve.
     * @param options - { decode?: boolean } - whether to decode the value (default: true).
     * @returns The query param value, or null if not found or URL is invalid.
     *
     * @example
     * const url = 'https://example.com/path?token=abc%20123';
     * queryParamUtils.getFromUrl(url, 'token'); // 'abc 123'
     */
    getFromUrl(url, key, options = {}) {
        try {
            const { decode = true } = options;
            const value = new URL(url).searchParams.get(key);
            return value && decode ? decodeURIComponent(value) : value;
        }
        catch {
            return null;
        }
    },
    /**
     * Get all query parameters as a key-value object.
     *
     * @param url - (Optional) URL string to extract params from. Defaults to window.location.
     * @returns An object of all query parameters.
     *
     * @example
     * // URL: https://site.com/?a=1&b=2
     * queryParamUtils.getAll(); // { a: '1', b: '2' }
     */
    getAll(url) {
        let params;
        if (url) {
            const parsed = new URL(url);
            params = parsed.searchParams;
        }
        else if (typeof window !== 'undefined') {
            params = new URLSearchParams(window.location.search);
        }
        else {
            return {};
        }
        const result = {};
        for (const [key, value] of params.entries()) {
            result[key] = value;
        }
        return result;
    },
    /**
     * Get multiple query param values at once by key list.
     *
     * @param keys - Array of keys to retrieve.
     * @param options - { decode?: boolean } - whether to decode the values (default: true).
     * @returns Object with each key mapped to its value (or null).
     *
     * @example
     * // URL: ?id=123&token=abc
     * queryParamUtils.getMany(['id', 'token']);
     * // => { id: '123', token: 'abc' }
     */
    getMany(keys, options = {}) {
        const result = {};
        for (const key of keys) {
            result[key] = this.get(key, options);
        }
        return result;
    },
    /**
     * Check if a specific query param key exists.
     *
     * @param key - Query param name to check.
     * @returns true if the key exists in the current URL, false otherwise.
     *
     * @example
     * // URL: ?id=123
     * queryParamUtils.has('id'); // true
     */
    has(key) {
        if (typeof window === 'undefined')
            return false;
        return new URLSearchParams(window.location.search).has(key);
    },
    /**
     * Extract query params from any given full URL string.
     *
     * @param url - Full URL string (e.g. https://example.com?a=1&b=2)
     * @returns An object of query params from that URL.
     *
     * @example
     * queryParamUtils.getAllFromUrl('https://site.com?x=10&y=20');
     * // => { x: '10', y: '20' }
     */
    getAllFromUrl(url) {
        const parsedUrl = new URL(url);
        const params = parsedUrl.searchParams;
        return Object.fromEntries(params.entries());
    },
    // ------------------------
    // SETTERS
    // ------------------------
    /**
     * Set or update a query parameter.
     *
     * @param key - Param name.
     * @param value - Value to set.
     * @param options - { encode?: boolean } - whether to encode the value (default: true).
     *
     * @example
     * queryParamUtils.set('user', 'john doe');
     * // URL => ?user=john+doe
     */
    set(key, value, options = {}) {
        if (typeof window === 'undefined')
            return;
        const { encode = true } = options;
        const params = new URLSearchParams(window.location.search);
        if (encode) {
            params.set(key, value);
        }
        else {
            params.set(key, decodeURIComponent(value));
        }
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    },
    /**
     * Append a value to an existing query param (e.g. multiple tags).
     *
     * @param key - Query key.
     * @param value - Value to append.
     * @param options - { encode?: boolean } - whether to encode value (default: true).
     *
     * @example
     * queryParamUtils.append('tag', 'react');
     * queryParamUtils.append('tag', 'js');
     * // => ?tag=react&tag=js
     */
    append(key, value, options = {}) {
        if (typeof window === 'undefined')
            return;
        const { encode = true } = options;
        const params = new URLSearchParams(window.location.search);
        if (encode) {
            params.append(key, value);
        }
        else {
            params.append(key, decodeURIComponent(value));
        }
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    },
    /**
     * Append multiple params to the current URL.
     *
     * @param baseUrl - Base URL to append to.
     * @param params - Object of params to append.
     * @returns URL with appended params.
     *
     * @example
     * queryParamUtils.appendParamsToUrl('https://site.com', { id: 1, tag: 'react' });
     * // => 'https://site.com?id=1&tag=react'
     */
    appendParamsToUrl(baseUrl, params) {
        // Separate the base URL from its query string if it exists
        const [urlWithoutQuery, existingQueryString] = baseUrl.split('?');
        const combinedParams = new URLSearchParams(existingQueryString || '');
        // Add new params (preserve existing)
        for (const [key, value] of Object.entries(params)) {
            if (value !== null && value !== undefined) {
                combinedParams.append(key, String(value));
            }
        }
        const finalQuery = combinedParams.toString();
        return finalQuery ? `${urlWithoutQuery}?${finalQuery}` : urlWithoutQuery;
    },
    // ------------------------
    // MODIFIERS
    // ------------------------
    /**
     * Remove a query param from the current URL.
     *
     * @param key - Query param to remove.
     *
     * @example
     * queryParamUtils.remove('ref');
     * // => URL without ?ref=...
     */
    remove(key) {
        if (typeof window === 'undefined')
            return;
        const params = new URLSearchParams(window.location.search);
        params.delete(key);
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    },
    /**
     * Remove all query parameters from the current URL.
     *
     * @example
     * // URL: ?id=123&token=abc
     * queryParamUtils.clear();
     * // => https://example.com/
     */
    clear() {
        if (typeof window === 'undefined')
            return;
        window.history.replaceState({}, '', window.location.pathname);
    },
    // ------------------------
    // BUILDERS
    // ------------------------
    /**
     * Build a query string from an object of key-value pairs.
     *
     * @param params - Object of params to serialize.
     * @param options - { withPrefix?: boolean } - include "?" prefix (default false).
     * @returns A query string like "a=1&b=2" or "?a=1&b=2"
     *
     * @example
     * queryParamUtils.buildQueryString({ id: 1, tag: 'react' });
     * // => "id=1&tag=react"
     *
     * queryParamUtils.buildQueryString({ id: 1 }, { withPrefix: true });
     * // => "?id=1"
     */
    buildQueryString(params, options = { withPrefix: false }) {
        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null) {
                search.set(key, String(value));
            }
        }
        const query = search.toString();
        return options.withPrefix ? (query ? `?${query}` : '') : query;
    },
};
